import React, { useState, useEffect } from "react";
import { ChatFramework } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { useChatStore } from "@/stores/chatStore";
import { FAQDetailData } from "@/types";
import { createApiUrl, API_CONFIG } from "@/config/api";
import { apiGet } from "@/utils/apiHelper";
import {
  trackEvent,
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
} from "@/utils/analytics";
import { Typography } from "@/components/fortress";

export const FAQDetailPage: React.FC = () => {
  const {
    selectedFaq,
    setSelectedFaq,
    setEntryPath,
    setCurrentView,
    openChat,
    closeChat,
  } = useChatStore();
  const [faq, setFaq] = useState<FAQDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  const handleBack = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "faq_detail",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "back",
    });

    setCurrentView("faq");
  };

  const handleStartChat = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "faq_detail",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "chat_with_us",
    });
    setEntryPath("faq");
    // NOTE: 切换到 ChatBox 视图并展开聊天窗口
    setCurrentView("chatbox");
    openChat();
  };

  const handleClose = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "faq_detail",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "close",
    });

    closeChat();
  };

  // NOTE: 获取 FAQ 详情
  useEffect(() => {
    if (!selectedFaq) {
      setCurrentView("faq");
      return;
    }

    const faqId = selectedFaq.id;

    const fetchFAQDetail = async () => {
      setLoading(true);
      try {
        const response = await apiGet<{ data: FAQDetailData }>(
          createApiUrl(API_CONFIG.ENDPOINTS.FAQ_DETAIL),
          { id: faqId },
        );

        setFaq(response.data);
      } catch (err) {
        console.error("Failed to fetch FAQ detail:", err);

        // NOTE: 使用模拟数据作为后备
        const mockFaqDetail: FAQDetailData = {
          id: faqId,
          name: "FAQ Detail",
          body: "This is the detailed content of the FAQ. If you need more help, please contact our customer service team.",
        };
        setFaq(mockFaqDetail);
        throw err;
      } finally {
        setLoading(false);
      }
    };

    if (faqId) {
      fetchFAQDetail();
    }

    return () => {
      setFaq(null);
      setSelectedFaq(null);
    };
  }, [selectedFaq]);

  return (
    <ChatFramework
      footer={
        // 开始聊天按钮
        <div className="h-[64px]">
          <Button
            onClick={handleStartChat}
            className="w-full h-full rounded-none"
            size="sm"
          >
            <MessageCircle className="w-4 h-4" />
            Chat with us!
          </Button>
        </div>
      }
      onBack={handleBack}
      onClose={handleClose}
      isLoading={loading}
    >
      {faq && (
        <div className="px-3 space-y-4">
          {/* FAQ 标题 */}
          <Typography level="h2">{faq.name}</Typography>

          {/* FAQ 内容 */}
          <Typography className="prose" level="caption1">
            <div dangerouslySetInnerHTML={{ __html: faq.body }} />
          </Typography>
        </div>
      )}
    </ChatFramework>
  );
};
